//Attachment section form landlord side

import React, { useRef, useState } from 'react';

import {
    Box,
    Typography,
    Button,
    Paper,
    Grid,
    CircularProgress,
    IconButton
} from '@mui/material';
import { Upload, Paperclip, Download, Eye } from 'react-feather';

const AttachmentsSection = ({ ticket, onUpload }) => {
    const [uploadLoading, setUploadLoading] = useState(false);
    const fileInputRef = useRef(null);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error("Date formatting error:", error);
            return 'Invalid date';
        }
    };

    // Trigger file input click
    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    // Handle file upload
    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploadLoading(true);
            const success = await onUpload(file);

            if (!success) {
                throw new Error("Upload failed");
            }
        } catch (error) {
            console.error("Error handling file upload:", error);
        } finally {
            setUploadLoading(false);
            // Reset input to allow uploading the same file again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Download attachment using base64 data
    const downloadAttachment = (attachment, index) => {
        if (!attachment.base64) {
            console.error("No base64 data available for this attachment");
            return;
        }

        try {
            // Determine MIME type for the file
            let mimeType = "application/octet-stream"; // Default MIME type
            if (attachment.file_type) {
                mimeType = attachment.file_type;
            } else if (attachment.file_name) {
                // Try to infer MIME type from filename extension
                const extension = attachment.file_name.split('.').pop().toLowerCase();
                const mimeTypes = {
                    pdf: 'application/pdf',
                    jpg: 'image/jpeg',
                    jpeg: 'image/jpeg',
                    png: 'image/png',
                    gif: 'image/gif',
                    doc: 'application/msword',
                    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    xls: 'application/vnd.ms-excel',
                    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    txt: 'text/plain'
                };
                mimeType = mimeTypes[extension] || mimeType;
            }

            // Determine file extension based on MIME type or original filename
            let fileExtension = '';
            if (attachment.file_name) {
                const parts = attachment.file_name.split('.');
                if (parts.length > 1) {
                    fileExtension = `.${parts.pop()}`;
                }
            } else if (attachment.file_type) {
                // Map MIME type to extension
                const mimeToExt = {
                    'application/pdf': '.pdf',
                    'image/jpeg': '.jpg',
                    'image/png': '.png',
                    'image/gif': '.gif',
                    'application/msword': '.doc',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
                    'application/vnd.ms-excel': '.xls',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
                    'text/plain': '.txt'
                };
                fileExtension = mimeToExt[attachment.file_type] || '';
            }

            // Create a download link with numbered filename
            const link = document.createElement('a');
            link.href = `data:${mimeType};base64,${attachment.base64}`;
            link.download = `Attachment_${(index !== undefined ? index + 1 : 1)}${fileExtension}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error downloading attachment:", error);
        }
    };

    // Preview attachment (for image types)
    const previewAttachment = (attachment, index) => {
        if (!attachment.base64) {
            console.error("No base64 data available for this attachment");
            return;
        }

        try {
            // Determine if this is an image type
            const isImage = attachment.file_type?.startsWith('image/') ||
                ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                    attachment.file_name?.split('.').pop().toLowerCase()
                );

            if (isImage) {
                // Open image in new tab
                const newTab = window.open();
                newTab.document.write(`
                    <html>
                        <head>
                            <title>Attachment ${index !== undefined ? index + 1 : 1}</title>
                            <style>body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; background: #f0f0f0; }</style>
                        </head>
                        <body>
                            <img src="data:${attachment.file_type || 'image/jpeg'};base64,${attachment.base64}" 
                                style="max-width: 95%; max-height: 95%; object-fit: contain;" />
                        </body>
                    </html>
                `);
                newTab.document.close();
            } else if (attachment.file_type === 'application/pdf' ||
                attachment.file_name?.toLowerCase().endsWith('.pdf')) {
                // Handle PDF preview
                const newTab = window.open();
                newTab.document.write(`
                    <html>
                        <head>
                            <title>Attachment ${index !== undefined ? index + 1 : 1}</title>
                            <style>body, html { margin: 0; height: 100%; }</style>
                        </head>
                        <body>
                            <embed src="data:application/pdf;base64,${attachment.base64}" 
                                type="application/pdf" width="100%" height="100%" />
                        </body>
                    </html>
                `);
                newTab.document.close();
            } else {
                // For other file types, just download
                downloadAttachment(attachment, index);
            }
        } catch (error) {
            console.error("Error previewing attachment:", error);
        }
    };

    // Get file type icon or preview image
    const getAttachmentIcon = (attachment) => {
        const isImage = attachment.file_type?.startsWith('image/') ||
            ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
                attachment.file_name?.split('.').pop().toLowerCase()
            );

        // For image files with base64 data, show a thumbnail
        if (isImage && attachment.base64) {
            return (
                <Box
                    component="div"
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '4px',
                        backgroundImage: `url(data:${attachment.file_type || 'image/jpeg'};base64,${attachment.base64})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        mr: 1
                    }}
                />
            );
        }

        // For non-image files, show icon
        return <Paperclip size={16} color="#6B7280" style={{ marginRight: 8 }} />;
    };

    return (
        <>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1
            }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Attachments</Typography>
                <Button
                    size="small"
                    startIcon={uploadLoading ? <CircularProgress size={16} /> : <Upload size={16} />}
                    onClick={triggerFileUpload}
                    disabled={uploadLoading}
                >
                    {uploadLoading ? "Uploading..." : "Upload File"}
                </Button>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                />
            </Box>

            {(!ticket.attachments || !ticket.attachments.filter(attachment => attachment.base64).length) ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="body2" color="textSecondary">No attachments</Typography>
                </Paper>
            ) : (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px'
                    }}
                >
                    <Grid container spacing={2}>
                        {ticket.attachments
                            .filter(attachment => attachment.base64) // Only show attachments with base64 data
                            .map((attachment, index) => (
                                <Grid item key={attachment.attachment_id || attachment.id} xs={12} sm={6} md={4}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'rgba(0,0,0,0.12)',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        {getAttachmentIcon(attachment)}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" noWrap>
                                                Attachment {index + 1}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Uploaded {formatDate(attachment.date_created || attachment.uploadedAt)}
                                            </Typography>
                                        </Box>

                                        {/* Preview Button (for images/PDFs) */}
                                        <IconButton
                                            size="small"
                                            onClick={() => previewAttachment(attachment, index)}
                                            sx={{ mr: 0.5 }}
                                        >
                                            <Eye size={16} />
                                        </IconButton>

                                        {/* Download Button */}
                                        <IconButton
                                            size="small"
                                            onClick={() => downloadAttachment(attachment, index)}
                                        >
                                            <Download size={16} />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            ))}
                    </Grid>
                </Paper>
            )}
        </>
    );
};

export default AttachmentsSection;